interface Props {
  code: string;
}

type Meta = { tone: "error" | "warn"; text: string };

const COPY: Record<string, Meta> = {
  // Admin allowlist gate (expat-api/src/auth/admin.ts)
  admin_disabled: {
    tone: "warn",
    text:
      "The admin allowlist is empty on the server (ADMIN_EMAILS unset).",
  },
  forbidden: {
    tone: "error",
    text:
      "Your email is not on the admin allowlist. Ask Isaac to add you to ADMIN_EMAILS.",
  },
  admin_email_unverified: {
    tone: "error",
    text:
      "Your sign-in provider didn't return a verified email — try a different Google account.",
  },

  // Client-side / sign-in
  not_signed_in: { tone: "warn", text: "You are signed out." },

  // Firebase auth middleware (expat-api/src/auth/middleware.ts)
  firebase_auth_unavailable: {
    tone: "error",
    text:
      "The API can't verify Firebase tokens right now (missing service-account key).",
  },
  missing_authorization_header: {
    tone: "error",
    text:
      "The API didn't see an Authorization header — try reloading the page.",
  },
  invalid_authorization_scheme: {
    tone: "error",
    text:
      "The Authorization header isn't a Bearer token — try reloading the page.",
  },
  missing_bearer_token: {
    tone: "error",
    text:
      "Your Bearer token was empty — try signing out and back in.",
  },
  invalid_token: {
    tone: "error",
    text:
      "Your Firebase ID token couldn't be verified — try signing out and back in.",
  },
  user_upsert_failed: {
    tone: "error",
    text:
      "The API couldn't store your user record — check the server logs.",
  },

  // Firebase token verification (expat-api/src/auth/firebase.ts)
  expired: {
    tone: "warn",
    text:
      "Your sign-in token expired — reload the page to refresh it.",
  },
  malformed_token: {
    tone: "error",
    text:
      "Your sign-in token is malformed — sign out and back in.",
  },
  bad_signature: {
    tone: "error",
    text:
      "Your sign-in token's signature didn't verify — sign out and back in.",
  },
  bad_algorithm: {
    tone: "error",
    text:
      "Your sign-in token used an unexpected algorithm — sign out and back in.",
  },
  missing_kid: {
    tone: "error",
    text:
      "Your sign-in token is missing its key id — sign out and back in.",
  },
  unknown_kid: {
    tone: "error",
    text:
      "Your sign-in token references an unknown Google signing key — try again in a minute.",
  },
  audience_mismatch: {
    tone: "error",
    text:
      "Your sign-in token was issued for a different Firebase project — sign out and back in.",
  },
  issuer_mismatch: {
    tone: "error",
    text:
      "Your sign-in token has the wrong issuer — sign out and back in.",
  },
  missing_subject: {
    tone: "error",
    text:
      "Your sign-in token is missing a subject — sign out and back in.",
  },
  issued_in_future: {
    tone: "error",
    text:
      "Your sign-in token claims a future issue time — check this device's clock.",
  },
  verification_disabled: {
    tone: "warn",
    text: "Token verification is disabled on the server.",
  },
  jwks_malformed: {
    tone: "error",
    text:
      "The API couldn't load Google's public signing keys — try again in a minute.",
  },

  // Admin route handlers (expat-api/src/routes/admin.ts)
  not_found: {
    tone: "error",
    text:
      "That record doesn't exist — it may have already been deleted or resolved.",
  },
  not_found_or_invalid_status: {
    tone: "error",
    text:
      "That restaurant either doesn't exist or isn't in a state that can be approved/rejected (it may have already been actioned).",
  },
  invalid_limit: {
    tone: "error",
    text: "The page-size parameter is invalid.",
  },
  invalid_offset: {
    tone: "error",
    text: "The pagination offset is invalid.",
  },
  invalid_resolved: {
    tone: "error",
    text: "The 'resolved' filter must be true or false.",
  },

  // AI-assist endpoints (expat-api/src/routes/admin.ts — parse-menu /
  // resolve-place). The owner supplies the API keys later, so the "not
  // configured" 503s are an expected state the UI must explain, not a crash.
  ai_not_configured: {
    tone: "warn",
    text:
      "AI menu parsing isn't configured yet — the owner hasn't added the Claude API key. You can still enter menu items by hand.",
  },
  places_not_configured: {
    tone: "warn",
    text:
      "Google Maps lookup isn't configured yet — the owner hasn't added the Places API key. You can still fill in the details by hand.",
  },
  ai_upstream_error: {
    tone: "error",
    text:
      "The menu parser couldn't read that image — try a clearer photo, or enter the items by hand.",
  },
  places_upstream_error: {
    tone: "error",
    text:
      "Google Maps lookup failed for that link — double-check the URL, or fill in the details by hand.",
  },
  image_base64_required: {
    tone: "error",
    text: "No image was attached — choose or drop a menu photo first.",
  },
  image_too_large: {
    tone: "error",
    text: "That menu photo is too large — use an image under ~15 MB.",
  },

  // Parse-from-links (expat-api /admin/parse-menu image_urls path). The server
  // fetches each menu image link itself; these surface fetch/validation issues.
  image_fetch_failed: {
    tone: "error",
    text:
      "Couldn't load one of the menu image links — check each URL is a direct, public image (not a webpage or a private/expired link).",
  },
  invalid_image_url: {
    tone: "error",
    text: "That menu image link is empty or invalid — use a direct http(s) image URL.",
  },
  invalid_image_urls: {
    tone: "error",
    text:
      "Add at least one valid menu image link (and no more than 10) before parsing.",
  },
  invalid_media_type: {
    tone: "error",
    text: "Unsupported image type — use a JPEG, PNG, GIF, or WebP photo.",
  },
  maps_url_required: {
    tone: "error",
    text: "Paste a Google Maps link first.",
  },
  maps_url_too_long: {
    tone: "error",
    text: "That Google Maps link is too long to process.",
  },

  // Menu image links (expat-api — restaurant create/update). The form now
  // manages a list of menu image URLs; these surface server-side validation.
  invalid_menu_image_urls: {
    tone: "error",
    text: "The menu image links must be a list of URLs.",
  },
  too_many_menu_images: {
    tone: "error",
    text: "Too many menu image links — keep it to 20 or fewer.",
  },
  invalid_menu_image_url: {
    tone: "error",
    text:
      "A menu image link is empty or too long — each must be a non-empty URL under 2000 characters.",
  },
};

function resolveCopy(code: string): Meta {
  const direct = COPY[code];
  if (direct) return direct;
  if (code.startsWith("jwks_fetch_failed_")) {
    return {
      tone: "error",
      text:
        "The API couldn't fetch Google's public signing keys — try again in a minute.",
    };
  }
  if (code.startsWith("http_")) {
    return {
      tone: "error",
      text:
        `Unexpected response from the API (${code}) — check the server logs.`,
    };
  }
  return { tone: "error", text: code };
}

export function ErrorBanner({ code }: Props) {
  const meta = resolveCopy(code);
  return <div className={`banner ${meta.tone}`}>{meta.text}</div>;
}
